import type { Comic, ComicSummary, OpLog, RevealAction, RoomSettings, RoomState, SheetReactions } from '@panels/shared';
import type { ActivityReport } from './activity.js';
import { serverClock } from './clock.js';
import { readIdentity, writeIdentity, type Identity } from './identity.js';

/**
 * The API is served from the app's own origin (a Pages Function in production;
 * Vite proxies /api to the local Worker in dev), so this is normally empty.
 * VITE_API_BASE exists only to point a build at an API somewhere else.
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }

  /** Worth retrying: the request never reached a decision. */
  get retryable(): boolean {
    return this.status === 0 || this.status >= 500 || this.status === 408 || this.status === 429;
  }
}

export type ClientRevealAction = Exclude<RevealAction, { type: 'titled' }>;

interface RequestOptions {
  method?: string;
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) {
    const identity = readIdentity();
    if (identity) headers.authorization = `Bearer ${identity.token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    // Status 0 means "we never got an answer" — always safe to retry.
    throw new ApiError(0, 'network', 'No connection');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const err = payload as { error?: string; message?: string } | null;
    throw new ApiError(response.status, err?.error ?? 'error', err?.message ?? 'Something went wrong');
  }
  return payload as T;
}

/* ------------------------------------------------------------- identity */

export async function createIdentity(): Promise<Identity> {
  const identity = await api<Identity>('/api/players', { method: 'POST', body: {}, auth: false });
  writeIdentity(identity);
  return identity;
}

/** Silent first-run identity creation (§10, screen 1). */
export async function ensureIdentity(): Promise<Identity> {
  const existing = readIdentity();
  if (existing) return existing;
  return createIdentity();
}

export async function setDisplayName(displayName: string): Promise<{ playerId: string; displayName: string }> {
  return api('/api/me', { method: 'PATCH', body: { displayName } });
}

export async function recoverIdentity(recoveryCode: string): Promise<Identity> {
  const identity = await api<Identity>('/api/recover', {
    method: 'POST',
    body: { recoveryCode },
    auth: false,
  });
  writeIdentity(identity);
  return identity;
}

/* ---------------------------------------------------------------- rooms */

export interface RoomListEntry {
  id: string;
  code: string;
  status: string;
  createdAt: string;
  active: boolean;
  memberCount: number;
}

export const listRooms = () => api<{ rooms: RoomListEntry[] }>('/api/rooms');

export const createRoom = (settings: Partial<RoomSettings>) =>
  api<RoomState>('/api/rooms', { method: 'POST', body: { settings } });

export const joinRoom = (code: string) => api<RoomState>('/api/rooms/join', { method: 'POST', body: { code } });

/**
 * A request that answers with room state, timed so the answer also refines our
 * estimate of the server clock (Round 3: synchronized replays).
 */
async function timedState<T extends RoomState | { state: RoomState }>(path: string, options: RequestOptions = {}): Promise<T> {
  const sentAt = serverClock.localNow();
  const result = await api<T>(path, options);
  const receivedAt = serverClock.localNow();
  const state: RoomState | undefined = 'state' in result ? result.state : (result as RoomState);
  if (state?.clock) {
    serverClock.add({ sentAt, serverReceived: state.clock.receivedMs, serverSent: state.clock.sentMs, receivedAt });
  }
  return result;
}

/** The poll. A drawing client adds its activity report (Round 2 §1.1). */
export function getRoomState(roomId: string, signal?: AbortSignal, activity?: ActivityReport | null) {
  const params = new URLSearchParams();
  if (activity) {
    params.set('panel', activity.panelId);
    params.set('actions', String(activity.actions));
    params.set('idleMs', String(Math.round(activity.idleMs)));
  }
  const qs = params.toString();
  return timedState<RoomState>(`/api/rooms/${roomId}/state${qs ? `?${qs}` : ''}`, { signal });
}

/** Anything anyone does during a co-viewed reveal (Round 3). Answers with the room state. */
export const revealAction = (roomId: string, action: ClientRevealAction) =>
  timedState<RoomState>(`/api/rooms/${roomId}/reveal`, { method: 'POST', body: { action } });

export const startRound = (roomId: string) => api<RoomState>(`/api/rooms/${roomId}/rounds`, { method: 'POST', body: {} });

export const voteSkip = (roomId: string, targetPlayerId: string) =>
  api<{ outcome: { passed: boolean; votes: number; votesNeeded: number }; state: RoomState }>(
    `/api/rooms/${roomId}/skip-votes`,
    { method: 'POST', body: { targetPlayerId } },
  );

/** "I'm in for another." Everyone ready starts the round by itself. */
export const setReady = (roomId: string, ready: boolean) =>
  api<{ started: boolean; state: RoomState }>(`/api/rooms/${roomId}/ready`, { method: 'POST', body: { ready } });

export const leaveRoom = (roomId: string) => api<{ ok: true }>(`/api/rooms/${roomId}/leave`, { method: 'POST', body: {} });

/** Host only. Stops any round in progress and closes the room. Comics survive. */
export const closeRoom = (roomId: string) =>
  api<{ ok: true; abandoned: number }>(`/api/rooms/${roomId}/close`, { method: 'POST', body: {} });

/* --------------------------------------------------------------- panels */

export const submitPanel = (panelId: string, ops: OpLog, idempotencyKey: string) =>
  api<{ status: 'submitted' | 'duplicate'; panelId: string }>(`/api/panels/${panelId}/submit`, {
    method: 'POST',
    body: { ops, idempotencyKey },
  });

/* --------------------------------------------------------------- comics */

export interface ComicQuery {
  players?: string[];
  /** Only comics this player has starred. */
  favorites?: boolean;
  shuffle?: boolean;
  roomId?: string;
  roundId?: string;
}

export function listComics(query: ComicQuery = {}): Promise<{ comics: ComicSummary[] }> {
  const params = new URLSearchParams();
  if (query.players?.length) params.set('players', query.players.join(','));
  if (query.favorites) params.set('favorites', '1');
  if (query.shuffle) params.set('shuffle', '1');
  if (query.roomId) params.set('roomId', query.roomId);
  if (query.roundId) params.set('roundId', query.roundId);
  const qs = params.toString();
  return api(`/api/comics${qs ? `?${qs}` : ''}`);
}

export const getComic = (sheetId: string) => api<Comic>(`/api/comics/${sheetId}`);

/** Everything about a comic except its drawings: cheap enough to poll for live titles. */
export const getComicSummary = (sheetId: string) => api<ComicSummary>(`/api/comics/${sheetId}/summary`);

export const setFavorite = (sheetId: string, favorite: boolean) =>
  api<{ sheetId: string; favorite: boolean }>(`/api/sheets/${sheetId}/favorite`, { method: favorite ? 'PUT' : 'DELETE' });

/* ------------------------------------------------------------ reactions */
// Backend only for now (Round 2 §3.1): nothing in the UI calls these yet.

export const getReactions = (sheetId: string) => api<SheetReactions>(`/api/sheets/${sheetId}/reactions`);

export const setReaction = (sheetId: string, panelId: string, kind: string, on: boolean) =>
  api<SheetReactions>(`/api/sheets/${sheetId}/panels/${panelId}/reactions/${encodeURIComponent(kind)}`, {
    method: on ? 'PUT' : 'DELETE',
  });

export const updateSheet = (sheetId: string, patch: { title?: string | null; hidden?: boolean }) =>
  api<Comic>(`/api/sheets/${sheetId}`, { method: 'PATCH', body: patch });

export const rotateShareToken = (sheetId: string) =>
  api<{ sheetId: string; shareToken: string }>(`/api/sheets/${sheetId}/rotate-share`, { method: 'POST', body: {} });

/* --------------------------------------------------------------- public */

export const getSharedComic = (token: string) => api<Comic>(`/api/share/comic/${token}`, { auth: false });

export const getSharedRoom = (token: string) =>
  api<{ room: { code: string; createdAt: string }; comics: ComicSummary[] }>(`/api/share/room/${token}`, {
    auth: false,
  });
